/**
 * Politique de mot de passe — règles partagées par les formulaires
 * d'inscription (pro, client, vendeur).
 *
 * Objectif : donner à l'utilisateur des messages CLAIRS et précis sur ce qui
 * ne va pas (au lieu d'un « mot de passe invalide » générique), et empêcher
 * les mots de passe trop faibles dès la saisie.
 */

export interface PasswordRule {
  id: string;
  label: string;
  /** Test de la règle sur le mot de passe courant. */
  test: (pw: string) => boolean;
}

/** Règles de robustesse appliquées (du plus simple au plus exigeant). */
export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: 'Au moins 8 caractères', test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'Au moins une majuscule (A-Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'Au moins une minuscule (a-z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'digit', label: 'Au moins un chiffre (0-9)', test: (pw) => /[0-9]/.test(pw) },
];

export interface PasswordCheckState extends PasswordRule {
  ok: boolean;
}

/** État de chaque règle pour le mot de passe courant. */
export function passwordChecks(pw: string): PasswordCheckState[] {
  return PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(pw) }));
}

/** Score de robustesse (0 → 4) : nombre de règles satisfaites. */
export function passwordScore(pw: string): number {
  return PASSWORD_RULES.reduce((n, r) => n + (r.test(pw) ? 1 : 0), 0);
}

/** Libellé + couleur de la barre de robustesse. */
export function passwordStrength(pw: string): { label: string; level: number; className: string; barClass: string } {
  const score = passwordScore(pw);
  if (!pw) return { label: '', level: 0, className: 'text-foreground-400', barClass: 'bg-background-200' };
  if (score <= 1) return { label: 'Très faible', level: 1, className: 'text-red-600', barClass: 'bg-red-500' };
  if (score === 2) return { label: 'Faible', level: 2, className: 'text-amber-600', barClass: 'bg-amber-500' };
  if (score === 3) return { label: 'Moyen', level: 3, className: 'text-accent-600', barClass: 'bg-accent-500' };
  return { label: 'Fort', level: 4, className: 'text-emerald-600', barClass: 'bg-emerald-500' };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Vrai si l'adresse e-mail a un format plausible. */
export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test((email || '').trim());
}

/**
 * Valide le mot de passe + sa confirmation.
 * Renvoie le premier message d'erreur lisible, ou `null` si tout est bon.
 */
export function validatePasswordPair(pw: string, confirm: string): string | null {
  if (!pw) return 'Le mot de passe est obligatoire.';
  const missing = PASSWORD_RULES.filter((r) => !r.test(pw)).map((r) => r.label.toLowerCase());
  if (missing.length > 0) {
    return `Mot de passe trop faible : ajoutez ${missing.join(', ')}.`;
  }
  if (pw !== confirm) return 'Les deux mots de passe ne correspondent pas.';
  return null;
}